import { STATI_EMAIL, type EmailInviata } from '@aulaviva/shared';
import { Alert, Badge, Button, DataTable, Dialog, EmptyState, NativeSelect, useToast, type ColumnDef } from '@aulaviva/ui';
import { CircleAlert, CircleCheck, Clock, Mail } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useEmailInviate, useReinviaEmail } from '@/api/amministrazione';
import { formattaDataOra } from '@/lib/date';
import { admin, amministrazione, comuni } from '@/testi';
import { CampoRicerca } from '../comuni';

const t = amministrazione.email;

export function SchedaEmail() {
  const toast = useToast();
  const [q, setQ] = useState('');
  const [stato, setStato] = useState('');
  const [template, setTemplate] = useState('');
  const [pagina, setPagina] = useState(1);
  const email = useEmailInviate({ q: q || undefined, stato: stato || undefined, template: template || undefined, pagina, perPagina: 50 });
  const reinvia = useReinviaEmail();
  const [daReinviare, setDaReinviare] = useState<EmailInviata | null>(null);

  const colonne = useMemo<ColumnDef<EmailInviata, unknown>[]>(
    () => [
      { header: t.colonne.data, cell: ({ row }) => formattaDataOra(row.original.at) },
      { header: t.colonne.destinatario, accessorKey: 'to' },
      { header: t.colonne.oggetto, cell: ({ row }) => row.original.oggetto ?? <span className="text-ink-muted">{t.senzaOggetto}</span> },
      { header: t.colonne.template, cell: ({ row }) => t.template[row.original.template] ?? row.original.template },
      {
        header: t.colonne.stato,
        cell: ({ row }) => {
          const s = row.original.stato;
          const v = s === 'inviata' ? 'success' : s === 'errore' ? 'error' : 'warning';
          const Icona = s === 'inviata' ? CircleCheck : s === 'errore' ? CircleAlert : Clock;
          return (
            <Badge variant={v} icon={Icona}>
              {t.stati[s]}
            </Badge>
          );
        },
      },
      {
        id: 'azioni',
        header: '',
        cell: ({ row }) =>
          row.original.reinviabile ? (
            <Button variant="discreet" onClick={() => setDaReinviare(row.original)}>
              {t.reinvia}
            </Button>
          ) : null,
      },
    ],
    [],
  );

  async function conferma() {
    if (!daReinviare) return;
    try {
      await reinvia.mutateAsync(daReinviare.id);
      setDaReinviare(null);
      toast.mostra({ variant: 'success', title: t.reinviata });
    } catch (e) {
      toast.mostra({ variant: 'error', title: e instanceof Error ? e.message : comuni.erroreGenerico });
    }
  }

  const vuoto = !q && !stato && !template;
  return (
    <>
      <p className="t-body text-ink-muted">{t.intro}</p>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_200px_240px] gap-4 items-end">
        <CampoRicerca valore={q} onCambia={(v) => { setQ(v); setPagina(1); }} hint={t.cercaAiuto} />
        <NativeSelect label={admin.comuni.filtroStato} placeholder={admin.comuni.tutti} value={stato} onChange={(e) => { setStato(e.target.value); setPagina(1); }} options={STATI_EMAIL.map((s) => ({ value: s, label: t.stati[s] }))} />
        <NativeSelect label={t.filtroTemplate} placeholder={admin.comuni.tutti} value={template} onChange={(e) => { setTemplate(e.target.value); setPagina(1); }} options={Object.entries(t.template).map(([value, label]) => ({ value, label }))} />
      </div>
      {email.isError && <Alert variant="error" title={email.error.message} />}
      <DataTable
        caption={amministrazione.schede.email}
        columns={colonne}
        data={email.data?.elementi ?? []}
        getRowId={(e) => e.id}
        loading={email.isPending}
        testi={admin.comuni}
        pagination={email.data ? { pagina: email.data.pagina, perPagina: email.data.perPagina, totale: email.data.totale, onPagina: setPagina } : undefined}
        empty={
          <EmptyState icon={Mail} title={vuoto ? t.nessunaTitolo : t.nessunRisultato}>
            {vuoto ? t.nessunaTesto : null}
          </EmptyState>
        }
      />
      {daReinviare?.errore && <p className="t-body-small text-error">{daReinviare.errore}</p>}
      <Dialog
        open={daReinviare !== null}
        onOpenChange={(o) => !o && setDaReinviare(null)}
        title={t.reinviaTitolo}
        description={daReinviare ? t.reinviaTesto(daReinviare.to) : ''}
        actions={
          <>
            <Button onClick={() => setDaReinviare(null)}>{admin.comuni.annulla}</Button>
            <Button variant="primary" onClick={conferma} loading={reinvia.isPending} loadingText={admin.comuni.inCorso}>
              {t.reinviaConferma}
            </Button>
          </>
        }
      />
    </>
  );
}
